import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Competition, PublishedEventListItem } from '../../@types';
import { useCompetitions } from '../../queries';
import { getDisplayName } from '../../utils';
import useEventsWithTopMarkets from '../useEventsWithTopMarkets';
import { useInPlayPage } from './useInPlayPage';

export const ALL_COMPETITIONS_VALUE = 'all';

export type NeobetInPlayTournamentGroup = {
  id: string;
  competition: Competition | null;
  tournament: PublishedEventListItem['tournament'];
  events: PublishedEventListItem[];
};

const normalizeCompetitionId = (competitionId: string | number | null | undefined) =>
  competitionId == null ? null : String(competitionId);

export const useNeobetInPlayPage = () => {
  const [selectedCompetitionId, setSelectedCompetitionId] = useState<string>(ALL_COMPETITIONS_VALUE);
  const competitionIds = selectedCompetitionId === ALL_COMPETITIONS_VALUE ? undefined : [selectedCompetitionId];
  const inPlayPage = useInPlayPage({ competitionIds });
  const eventsWithLiveMarkets = useEventsWithTopMarkets(inPlayPage.events, inPlayPage.topMarkets);

  const { data: competitionsData, isLoading: isCompetitionsLoading } = useCompetitions({
    sportId: inPlayPage.sportId,
    type: 'match',
    isLive: true,
  });

  useEffect(() => {
    setSelectedCompetitionId(ALL_COMPETITIONS_VALUE);
  }, [inPlayPage.sportId]);

  const handleSelectedCompetitionIdChange = useCallback((nextCompetitionId: string) => {
    setSelectedCompetitionId(normalizeCompetitionId(nextCompetitionId) ?? ALL_COMPETITIONS_VALUE);
  }, []);

  const fallbackCompetitions = useMemo(() => {
    const seenCompetitionIds = new Set<string>();

    return inPlayPage.events.reduce<Competition[]>((accumulator, event) => {
      const competition = event.competition ?? event.tournament.competition;
      const normalizedCompetitionId = normalizeCompetitionId(competition?.id);

      if (!competition || !normalizedCompetitionId || seenCompetitionIds.has(normalizedCompetitionId)) {
        return accumulator;
      }

      seenCompetitionIds.add(normalizedCompetitionId);
      accumulator.push({
        ...competition,
        id: normalizedCompetitionId,
        name: getDisplayName(competition),
      });

      return accumulator;
    }, []);
  }, [inPlayPage.events]);

  const competitions = useMemo(() => {
    if (!competitionsData?.items?.length) {
      return fallbackCompetitions;
    }

    const seenCompetitionIds = new Set<string>();

    return competitionsData.items.reduce<Competition[]>((accumulator, competition) => {
      const normalizedCompetitionId = normalizeCompetitionId(competition.id);

      if (!normalizedCompetitionId || seenCompetitionIds.has(normalizedCompetitionId)) {
        return accumulator;
      }

      seenCompetitionIds.add(normalizedCompetitionId);
      accumulator.push({
        ...competition,
        id: normalizedCompetitionId,
        name: getDisplayName(competition),
      });

      return accumulator;
    }, []);
  }, [competitionsData, fallbackCompetitions]);

  useEffect(() => {
    if (selectedCompetitionId === ALL_COMPETITIONS_VALUE) {
      return;
    }

    if (!competitions.some((competition) => competition.id === selectedCompetitionId)) {
      setSelectedCompetitionId(ALL_COMPETITIONS_VALUE);
    }
  }, [competitions, selectedCompetitionId]);

  const tournamentGroups = useMemo<NeobetInPlayTournamentGroup[]>(() => {
    const groupsById = new Map<string, NeobetInPlayTournamentGroup>();

    eventsWithLiveMarkets.forEach((event) => {
      const existingGroup = groupsById.get(event.tournament.id);

      if (existingGroup) {
        existingGroup.events.push(event);
        return;
      }

      groupsById.set(event.tournament.id, {
        id: event.tournament.id,
        competition: event.competition ?? event.tournament.competition ?? null,
        tournament: event.tournament,
        events: [event],
      });
    });

    return Array.from(groupsById.values());
  }, [eventsWithLiveMarkets]);

  return {
    ...inPlayPage,
    competitions,
    isCompetitionsLoading: Boolean(inPlayPage.sportId) && isCompetitionsLoading,
    selectedCompetitionId,
    setSelectedCompetitionId: handleSelectedCompetitionIdChange,
    filteredEvents: inPlayPage.events,
    tournamentGroups,
  };
};
